/**
 * 评论 @ 提及：文本解析与拼装
 *
 * 存储约定（前后端一致，改这里等于改接口契约）：
 *   - 评论正文里存的是 "@昵称" 明文，和用户在输入框里看到的完全一样；
 *   - 被 @ 的人另外用 atUserIds（英文逗号分隔的用户 id）单独传，后端字段同名，
 *     解析逻辑见 pp-back 的 com.shiyi.utils.AtUserUtils。
 *
 * 为什么要存两份，而不是只留正文靠正则反查用户：
 * 昵称可以改、可以带空格、本身也可能含 "@"，纯正则一定会错配到别人身上 ——
 * 点一下跳到陌生人主页是小事，把通知发错人就是事故了。所以正文只负责给人看，
 * 谁被 @ 了一律以 id 列表为准。
 *
 * 渲染侧反过来要用「后端回传的最新昵称」去正文里定位片段。用户改名后旧昵称匹配不上，
 * 那一段就退化成不可点击的纯文本 —— 这是有意的降级：正文是发布那一刻的快照，
 * 不能因为对方改名就去改别人评论的正文。
 */

// 与后端 AtUserUtils.SEPARATOR 一致
const SEPARATOR = ','

// 与后端 AtUserUtils.MAX_AT_USER_COUNT 一致。前端先拦一道，给用户即时反馈，
// 越过这道限制的请求后端会直接拒掉
export const MAX_AT_USER_COUNT = 10

// @ 后面最多允许多少个字符还算「正在搜人」。
// 超过就认为用户只是在打一段带 @ 的普通文本（比如邮箱），不再弹选人面板
const MENTION_QUERY_MAX_LENGTH = 20

/**
 * 洗掉不能用于渲染的项：没有 id、没有昵称的，以及重复的 id。
 *
 * @param {Array} atUsers 后端返回的 atUsers 或本地维护的已选列表
 * @returns {Array<{userId: String, nickname: String, cancelled: Boolean}>}
 */
export function normalizeAtUsers (atUsers) {
  if (!Array.isArray(atUsers) || !atUsers.length) return []
  const seen = new Set()
  const result = []
  atUsers.forEach(item => {
    if (!item) return
    // 统一口径是 userId：评论列表的 atUsers、@ 候选人接口、本地已选列表都用这个字段名。
    // 留 item.id 兜底是给「传进来的是一份用户对象」这种调用方式留的余地，
    // 当前所有调用方都走 userId
    const userId = String(item.userId || item.id || '')
    const nickname = String(item.nickname || '')
    if (!userId || !nickname || seen.has(userId)) return
    seen.add(userId)
    result.push({ userId, nickname, cancelled: item.cancelled === true })
  })
  return result
}

/**
 * 把正文切成可渲染的片段序列。
 *
 * @param {String} content 评论正文
 * @param {Array}  atUsers 该评论被 @ 的用户（后端 atUsers 字段）
 * @returns {Array<{type: 'text'|'at', text: String, userId?: String, cancelled?: Boolean}>}
 *          没有 @ 时返回单个 text 片段；正文为空时返回空数组
 */
export function parseMentionSegments (content, atUsers) {
  const text = content == null ? '' : String(content)
  if (!text) return []

  const mentions = normalizeAtUsers(atUsers)
  if (!mentions.length) return [{ type: 'text', text }]

  // 长昵称优先。"张三" 和 "张三丰" 同时被 @ 时，必须先试 "@张三丰"，
  // 否则 "@张三丰" 会被切成 "@张三" + "丰"，点进去是另一个人。
  const candidates = mentions.slice().sort((a, b) => b.nickname.length - a.nickname.length)

  // 已经用掉的候选。b_user.nickname 上没有唯一约束，两个人可以同名，
  // 正文里就是 "@小明 @小明"。不记录已用过的，find 每次都返回同一个候选：
  // 两个链接指向同一人，而另一个人在 syncAtUsers 里会被当成"正文里没有他"剔掉，
  // 结果是他收不到通知。按出现顺序消费，第一个 @ 给第一个人，第二个给第二个人。
  const consumed = new Set()

  const segments = []
  let buffer = ''
  let i = 0

  const flush = () => {
    if (buffer) {
      segments.push({ type: 'text', text: buffer })
      buffer = ''
    }
  }

  while (i < text.length) {
    if (text.charAt(i) === '@') {
      const matched = candidates.filter(item => text.startsWith('@' + item.nickname, i))
      // 优先挑还没用过的；同名的都用完了就退回第一个（宁可两个链接同人，也不要漏掉这一段不渲染）
      const hit = matched.find(item => !consumed.has(item.userId)) || matched[0]
      if (hit) {
        flush()
        consumed.add(hit.userId)
        segments.push({
          type: 'at',
          text: '@' + hit.nickname,
          userId: hit.userId,
          cancelled: hit.cancelled
        })
        i += hit.nickname.length + 1
        continue
      }
    }
    buffer += text.charAt(i)
    i += 1
  }
  flush()

  return segments
}

/**
 * 判断用户是不是正在输入 "@xxx" 想搜人。
 *
 * 只认「正文末尾最后一个 @ 之后的内容」这一种情况，不去读光标位置：
 * uni-app 的 textarea 在 @input 里给的 e.detail.cursor 跨端并不可靠
 * （部分平台压根不给），拿它算 @ 的作用域会在真机上莫名失效。
 * 用户把光标插到中间想 @ 人时，走工具栏的 @ 按钮，那条路径不依赖光标。
 *
 * @param {String} content 输入框当前内容
 * @returns {{index: Number, keyword: String}|null} index 是 '@' 在正文里的下标
 */
export function detectMentionQuery (content) {
  const text = content == null ? '' : String(content)
  const index = text.lastIndexOf('@')
  if (index === -1) return null

  // 打完几个字紧接着 @ 别人是必须支持的（"你好@张三"），所以这里不要求 '@' 前面有空格。
  // 但完全不判断的话，输入邮箱 "abc@..." 打到 "abc@" 就会弹出选人面板。
  // 折中：只在「'@' 前面那一串看起来像邮箱账号」时不触发，见 looksLikeEmailLocalPart。
  if (looksLikeEmailLocalPart(text, index)) return null

  const keyword = text.slice(index + 1)
  // 出现空白说明这个 @ 已经"用完"了（要么已经选完人，要么本来就是普通文本）
  if (/\s/.test(keyword)) return null
  if (keyword.length > MENTION_QUERY_MAX_LENGTH) return null

  return { index, keyword }
}

/**
 * 判断 '@' 前面那一串是不是像邮箱的账号部分。
 *
 * 判据：紧贴 '@' 往前取到第一个空白为止，这一段非空、且只由邮箱账号允许的
 * ASCII 字符组成（字母数字和 . _ % + -）。
 *
 * 于是：
 *   "你好@"      前面是中文  → 不像邮箱 → 触发选人（这是要支持的主场景）
 *   "abc@"       全是字母    → 像邮箱   → 不触发
 *   "a1._%+-@"   全是合法符  → 像邮箱   → 不触发
 *   "@" / "你好 @" 前面为空  → 不像邮箱 → 触发
 *
 * 代价是英文词后面直接 @ 人（"hello@张三"）不会自动弹面板，这种情况走工具栏的 @ 按钮。
 * 在一个中文校园社区里，「中文字后面接着 @」远比「英文词后面接着 @」常见，
 * 而邮箱误触发是每个人都会遇到的。
 *
 * @param {String} text  完整正文
 * @param {Number} index '@' 的下标
 * @returns {Boolean}
 */
function looksLikeEmailLocalPart (text, index) {
  if (index <= 0) return false
  let start = index
  while (start > 0 && !/\s/.test(text.charAt(start - 1))) {
    start -= 1
  }
  const localPart = text.slice(start, index)
  if (!localPart) return false
  return /^[A-Za-z0-9._%+-]+$/.test(localPart)
}

/**
 * 找出覆盖指定下标的那段 @ 提及。
 *
 * 用于「按一次退格整段删掉 @昵称」：光标停在 "@张三" 后面按退格，
 * 原生行为是只删掉"三"，剩下 "@张" 变成一段无意义的文本，用户得再按四次。
 * 调用方拿到 {start, end} 后把整段一起删掉。
 *
 * @param {String} content 正文
 * @param {Array}  atUsers 该正文里被 @ 的用户
 * @param {Number} index   要检测的字符下标
 * @returns {{start: Number, end: Number, userId: String, text: String}|null}
 *          index 不在任何 @ 片段内时返回 null
 */
export function findMentionAt (content, atUsers, index) {
  const text = content == null ? '' : String(content)
  if (typeof index !== 'number' || index < 0 || index >= text.length) return null

  let cursor = 0
  const segments = parseMentionSegments(text, atUsers)
  for (let i = 0; i < segments.length; i += 1) {
    const seg = segments[i]
    const start = cursor
    const end = cursor + seg.text.length
    if (seg.type === 'at' && index >= start && index < end) {
      return { start, end, userId: seg.userId, text: seg.text }
    }
    cursor = end
  }
  return null
}

/**
 * 找出「删掉 index 这个字符」该归属于哪一段 @ 提及，范围含它后面紧跟的那个空格。
 *
 * 和 findMentionAt 的区别，就是把 "@昵称" 和它后面那个空格当成一个整体（token）看：
 *
 *   - 插入提及时统一在后面补了一个空格（见 insertMention），所以正文里是 "@昵称 XX"。
 *     用户从后往前删，删完 XX 之后碰到的是那个空格。如果这时只是把空格删掉，
 *     用户得再按一次才开始处理提及 —— 而那个空格是看不见的，用户的感受是
 *     "按了一下没反应"。主流 App（微信、小红书）都把它算进提及里：
 *     删到这个空格时整段就进入选中态，再按一次整段消失。
 *
 *   - 返回值里 end 和 tokenEnd 分开：
 *       end      到昵称结尾，用来画选中背景 —— 把空格也涂上会在名字后面多出一块，
 *                看着像还跟了别的内容；
 *       tokenEnd 含尾随空格，用来判定「还在删这一段」以及真正删除的范围 ——
 *                不含的话 "@甲 @乙" 删掉 @乙 会留下 "@甲  " 两个空格。
 *
 * @param {String} content 正文
 * @param {Array}  atUsers 该正文里被 @ 的用户
 * @param {Number} index   被删掉的那个字符的下标
 * @returns {{start: Number, end: Number, tokenEnd: Number, userId: String, text: String}|null}
 *          index 既不在提及内、也不是紧跟提及的那个空格时返回 null
 */
export function findMentionTokenAt (content, atUsers, index) {
  const text = content == null ? '' : String(content)
  let mention = findMentionAt(text, atUsers, index)

  if (!mention && text.charAt(index) === ' ') {
    // 落在某段提及后面紧跟的那个空格上。要求 end === index，
    // 即这个空格确实紧贴着提及，而不是隔了别的字符
    const before = findMentionAt(text, atUsers, index - 1)
    if (before && before.end === index) mention = before
  }
  if (!mention) return null

  const tokenEnd = text.charAt(mention.end) === ' ' ? mention.end + 1 : mention.end

  return {
    start: mention.start,
    end: mention.end,
    tokenEnd,
    userId: mention.userId,
    text: mention.text
  }
}

/**
 * 把选中的人插进正文。
 *
 * 两种调用场景：
 *   1) 用户输入 "@" 唤起面板 —— 传 triggerIndex，把 "@已输入的搜索词" 整段替换掉，
 *      否则会变成 "@张@张三 "；
 *   2) 用户点工具栏的 @ 按钮 —— 不传 triggerIndex，直接追加到末尾。
 *
 * 末尾补一个空格：一是让 "@昵称" 和后面的话分开，二是让 detectMentionQuery
 * 不再把它当成"正在搜人"，面板不会选完人又立刻弹回来。
 *
 * @param {String} content      当前正文
 * @param {String} nickname     被 @ 的人的昵称
 * @param {Number} triggerIndex 触发面板的那个 '@' 的下标，没有则传 -1/undefined
 * @returns {String} 插入后的正文
 */
export function insertMention (content, nickname, triggerIndex) {
  const text = content == null ? '' : String(content)
  const mention = '@' + String(nickname || '')

  const validTrigger = typeof triggerIndex === 'number'
    && triggerIndex >= 0
    && triggerIndex < text.length
    && text.charAt(triggerIndex) === '@'

  if (!validTrigger) {
    // 追加到末尾时前面也补一个空格，否则会粘成 "你好@张三"，
    // 读起来像句子的一部分而不是一个提及
    const prefix = (text && !/\s$/.test(text)) ? ' ' : ''
    return text + prefix + mention + ' '
  }

  // 触发点之后到第一个空白字符之前都是搜索词，一并被替换
  let end = triggerIndex + 1
  while (end < text.length && !/\s/.test(text.charAt(end))) {
    end += 1
  }
  const rest = text.slice(end)
  // 后面已经紧跟空白了就别再补一个，否则光标插在句子中间时会留下两个空格
  const suffix = /^\s/.test(rest) ? '' : ' '
  return text.slice(0, triggerIndex) + mention + suffix + rest
}

/**
 * 用正文校准已选的 @ 列表。
 *
 * 用户可能把 "@张三" 手动删掉一部分或整段删掉，此时不该再把张三提交上去
 * （他会收到一条 @ 通知，但评论里根本找不到自己的名字）。每次输入后调一次，
 * 以正文为唯一事实来源。
 *
 * @param {String} content 输入框当前内容
 * @param {Array}  atUsers 本地维护的已选列表
 * @returns {Array} 正文里仍然存在的那些
 */
export function syncAtUsers (content, atUsers) {
  const mentions = normalizeAtUsers(atUsers)
  if (!mentions.length) return []

  // 复用和渲染完全相同的切分逻辑来判断"正文里还有没有这个人"，
  // 而不是简单地 indexOf('@' + 昵称)：
  // "@张三丰" 这个串里含有 "@张三"，用户明明已经把 "@张三" 删掉了，
  // indexOf 却依然命中，结果给张三发了一条他名字都不在里面的 @ 通知。
  const survived = new Set()
  parseMentionSegments(content, mentions).forEach(seg => {
    if (seg.type === 'at' && seg.userId) survived.add(seg.userId)
  })

  return mentions.filter(item => survived.has(item.userId))
}

/**
 * 拼成提交给后端的 atUserIds。
 *
 * @param {Array} atUsers 已选列表
 * @returns {String} 逗号分隔的 id 串；没有 @ 任何人时返回空串
 */
export function buildAtUserIds (atUsers) {
  return normalizeAtUsers(atUsers).map(item => item.userId).join(SEPARATOR)
}
